import React, { Fragment } from 'react';
import { Badge, Col, Form, Row, Table } from 'react-bootstrap';
import { FaCar } from 'react-icons/fa';
import { BsBriefcase, BsCardHeading, BsDiagram3, BsExclamationTriangleFill, BsJournalText, BsPeopleFill, BsShieldCheck } from 'react-icons/bs';
import {
    estadoSeccion7, formatoFechaISO, formatoMontoInforme, formatoSiNo, glosaCombo, MSG_SECCION7_PENDIENTE
} from './utilsInformePenal';

const estiloModificado = { backgroundColor: '#FFF3CD', borderColor: '#FFC107' };

// Un campo de solo lectura; si el atributo cambió respecto a la versión anterior se destaca con «Modificado» (RN-13).
const Campo = ({ label, valor, md = 3, modificado = false, area = false }) => (
    <Col md={md} className="mb-2">
        <Form.Label className="fw-bold" style={{ fontSize: '0.85rem' }}>
            {label} {modificado && <Badge bg="warning" text="dark">Modificado</Badge>}
        </Form.Label>
        <Form.Control
            as={area ? 'textarea' : 'input'}
            rows={area ? 4 : undefined}
            readOnly
            disabled
            value={valor === null || valor === undefined ? '' : valor}
            style={modificado ? estiloModificado : undefined}
        />
    </Col>
);

// Sección delimitada: caja con borde y encabezado de color, como el formulario «Informe Inicial» del abogado (prototipo RG-576).
// La sección 7 lleva un tono más claro porque no la ingresa el abogado.
const COLOR_SECCION = '#4F517A';
const COLOR_SECCION_7 = '#9095b8';

const Seccion = ({ titulo, icono, children, colorEncabezado = COLOR_SECCION, sinRelleno = false }) => (
    <div className="mb-3" style={{ border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ background: colorEncabezado, color: 'white', padding: '8px 15px', fontWeight: 500, fontSize: '0.9rem' }}>
            {icono ? <span className="me-2" style={{ verticalAlign: '-2px' }}>{icono}</span> : null}{titulo}
        </div>
        <div style={{ background: 'white', padding: sinRelleno ? 0 : '15px 18px' }}>{children}</div>
    </div>
);

// Nombre del implicado para la tabla de montos de la sección 7 (el servidor solo envía el id; el nombre sale de la sección 1).
const nombreImplicado = (implicados, id) => {
    const implicado = (implicados || []).find((i) => i.idImplicado === id);
    return implicado && implicado.nombre !== null && implicado.nombre !== undefined ? implicado.nombre : '';
};

// Informe de defensa penal en solo lectura (secciones 1 a 7). `datos` es el objeto del modo `detalle` del lambda
// defensa-fnc-obtiene-informe-penal: { informe, combos, implicados, fiscalias, seccion7, versionAnterior, modificados }.
// `modoRevision` (RG-580; por defecto false = comportamiento anterior): lo usa la pantalla de revisión del analista, que pinta
// su propia leyenda de versión y su propia sección 7 editable. Con `true` el componente NO muestra la línea de versión ni el
// bloque «7. Decisión Compañía» (ni su título); no cambia estadoSeccion7.
const InformePenalLectura = ({ datos, modoRevision = false }) => {
    if (!datos || !datos.informe) return null;

    const { informe, combos, implicados, fiscalias, seccion7, modificados } = datos;
    const esModificado = (atributo) => Array.isArray(modificados) && modificados.includes(atributo);
    const fiscalia = (fiscalias || []).find((f) => f.instancia === informe.instanciaJudicial);
    const mostrarSeccion7 = estadoSeccion7(informe.estado, seccion7, datos.perfil, informe.nroVersion);

    return (
        <div>
            {/* Aviso de actualización (prototipo): solo si hay versión anterior con la que comparar. La versión 1 va en el título del modal. */}
            {!modoRevision && datos.versionAnterior ? (
                <div className="mb-3" style={{ background: '#fff3cd', border: '1px solid #ffecb5', color: '#664d03', borderRadius: 4, padding: '8px 12px', fontSize: '0.85rem' }}>
                    <BsExclamationTriangleFill className="me-2" />
                    Versión {informe.nroVersion} (Actualización). Los campos resaltados{' '}
                    <span style={{ background: '#d4a842', color: '#4a3c00', fontSize: '0.7rem', fontWeight: 700, padding: '1px 8px', borderRadius: 10 }}>Modificado</span>{' '}
                    cambiaron respecto a la Versión {datos.versionAnterior}.
                </div>
            ) : null}

            <Seccion titulo="1. Implicados" icono={<BsPeopleFill />} sinRelleno>
                <Table size="sm" bordered className="mb-0">
                    <thead>
                        <tr>
                            <th>ID</th><th>Nombre</th><th>Calidad de la Víctima</th><th>Edad</th><th>Tipo Lesión</th><th>Fecha Actualización</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(implicados || []).length === 0
                            ? <tr><td colSpan={6} className="text-center">Sin implicados</td></tr>
                            : implicados.map((i) => (
                                <tr key={i.idImplicado}>
                                    <td>{i.idImplicado}</td>
                                    <td>{i.nombre}</td>
                                    <td>{i.calidadVictima}</td>
                                    <td>{i.edad === null || i.edad === undefined ? '' : i.edad}</td>
                                    <td>{i.tipoLesion}</td>
                                    <td>{i.fechaActualizacion}</td>
                                </tr>
                            ))}
                    </tbody>
                </Table>
            </Seccion>

            <Seccion titulo="2. Hechos del Siniestro" icono={<FaCar />}>
                <Row>
                    <Campo label="Tipo de Delito" valor={glosaCombo(combos.tipoDelito, informe.tipoDelito)} modificado={esModificado('tipoDelito')} />
                    <Campo label="Intervino Tránsito" valor={formatoSiNo(informe.intervinoTransito)} modificado={esModificado('intervinoTransito')} />
                    <Campo label="Agravantes" valor={glosaCombo(combos.agravante, informe.agravante)} modificado={esModificado('agravante')} />
                    <Campo label="N° de Lesionados" valor={informe.nroLesionados} modificado={esModificado('nroLesionados')} />
                    <Campo label="N° de Muertos" valor={informe.nroMuertos} modificado={esModificado('nroMuertos')} />
                    <Campo label="Grado de Responsabilidad" valor={glosaCombo(combos.gradoResponsabilidad, informe.gradoResponsabilidad)} modificado={esModificado('gradoResponsabilidad')} />
                </Row>
            </Seccion>

            <Seccion titulo="3. Validación de Licencia" icono={<BsCardHeading />}>
                <Row>
                    <Campo label="¿Licencia Vigente?" valor={formatoSiNo(informe.licenciaVigente)} modificado={esModificado('licenciaVigente')} />
                    <Campo label="Categoría Licencia" valor={glosaCombo(combos.categoriaLicencia, informe.categoriaLicencia)} modificado={esModificado('categoriaLicencia')} />
                    <Campo label="Vigencia Licencia" valor={formatoFechaISO(informe.vigenciaLicencia)} modificado={esModificado('vigenciaLicencia')} />
                </Row>
            </Seccion>

            <Seccion titulo="4. Hipótesis de Responsabilidad" icono={<BsDiagram3 />}>
                <Row>
                    <Campo label="Hipótesis Asegurado" md={6} valor={glosaCombo(combos.hipotesisAsegurado, informe.hipotesisAsegurado)} modificado={esModificado('hipotesisAsegurado')} />
                    <Campo label="Hipótesis Tercero" md={6} valor={glosaCombo(combos.hipotesisTercero, informe.hipotesisTercero)} modificado={esModificado('hipotesisTercero')} />
                </Row>
            </Seccion>

            <Seccion titulo="5. Hechos" icono={<BsJournalText />}>
                <Row>
                    <Campo label="Hechos (relato + consulta RUNT)" md={12} area valor={informe.hechos} modificado={esModificado('hechos')} />
                </Row>
            </Seccion>

            <Seccion titulo="6. Estrategia Jurídica y Recomendaciones" icono={<BsBriefcase />}>
                <Row>
                    <Campo label="Estrategia" md={12} area valor={informe.estrategia} modificado={esModificado('estrategia')} />
                    <Campo label="Fiscalía" md={6} valor={fiscalia ? fiscalia.nombre : ''} modificado={esModificado('instanciaJudicial')} />
                    <Campo label="No. Radicado Fiscalía" md={6} valor={informe.radicado} modificado={esModificado('radicado')} />
                </Row>
            </Seccion>

            {!modoRevision && mostrarSeccion7 !== 'OCULTA' && (
                <Seccion titulo="7. Decisión Compañía" icono={<BsShieldCheck />} colorEncabezado={COLOR_SECCION_7}>
                    {mostrarSeccion7 === 'PENDIENTE'
                        ? <div className="text-muted fst-italic">{MSG_SECCION7_PENDIENTE}</div>
                        : (
                            <>
                                <Row>
                                    <Campo label="Decisión de la Compañía" md={6} valor={glosaCombo(combos.decisionCompania, seccion7.decisionCompania)} />
                                    <Campo label="Fecha de Revisión" md={3} valor={seccion7.fechaRevision} />
                                </Row>
                                {(seccion7.montosConciliar || []).length > 0 && (
                                    <Table size="sm" bordered className="mt-2">
                                        <thead><tr><th style={{ width: 70 }}>ID</th><th>Nombre Implicado</th><th style={{ width: 200 }}>Monto a Conciliar</th></tr></thead>
                                        <tbody>
                                            {seccion7.montosConciliar.map((m) => (
                                                <tr key={m.idImplicado}>
                                                    <td>{m.idImplicado}</td>
                                                    <td>{nombreImplicado(implicados, m.idImplicado)}</td>
                                                    <td>$ {formatoMontoInforme(m.monto)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                                <Row>
                                    <Campo label="Comentario / Instrucción al Abogado" md={12} area valor={seccion7.comentarioInstruccion} />
                                </Row>
                            </>
                        )}
                </Seccion>
            )}
        </div>
    );
}

export default InformePenalLectura;
